import { useEffect, useRef } from 'react';

interface Options extends IntersectionObserverInit {
  once?: boolean;
}

/**
 * Fires `onIntersect` when the returned ref's element enters the viewport.
 * Disconnects after the first intersection when `once` is true (default).
 */
export function useIntersectionObserver<T extends Element>(
  onIntersect: (entry: IntersectionObserverEntry) => void,
  { once = true, threshold = 0.1, rootMargin }: Options = {},
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        onIntersect(entry);
        if (once) observer.disconnect();
      },
      { threshold, rootMargin },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [onIntersect, once, threshold, rootMargin]);

  return ref;
}
